import React, { useCallback } from 'react';
import type { PageDetailOut } from '../../types/catalog';
import type { ContentWithSections, SectionVocabCards, VocabCardItem } from '../../types/pageTypes';
import { Volume2 } from 'lucide-react';
import { speakTamil, speakTamilSequence } from '../../utils/tts';
import { ImageWithFallback } from '@/components/common/ImageWithFallback';

interface VocabPictureCardsV2Props {
  page: PageDetailOut;
  onComplete?: () => void;
}

function getSections(page: PageDetailOut) {
  const content = page.page_json_v1?.content as ContentWithSections | undefined;
  return content?.sections ?? [];
}

function hasPlayAll(interactions: unknown[] | undefined) {
  return (interactions ?? []).some(
    (i) => typeof i === 'object' && i != null && (i as { type?: string }).type === 'tts.play_all'
  );
}

export const VocabPictureCardsV2: React.FC<VocabPictureCardsV2Props> = ({ page }) => {
  const sections = getSections(page);
  const vocab = sections.find((s) => s.type === 'vocab_cards') as SectionVocabCards | undefined;
  const items = vocab?.items ?? vocab?.cards ?? [];
  const playAllEnabled = hasPlayAll(page.page_json_v1?.interactions as unknown[] | undefined);

  const playAll = useCallback(() => {
    const texts = items.map((c) => (c.tts ?? c.text ?? c.word)?.trim()).filter(Boolean) as string[];
    speakTamilSequence(texts);
  }, [items]);

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
        {String(page.page_title ?? (page.page_json_v1 as { page_title?: string } | undefined)?.page_title ?? 'Vocabulary')}
      </h1>

      {vocab?.title && (
        <h2 className="text-lg font-medium text-gray-800 dark:text-gray-200">{vocab.title}</h2>
      )}

      {playAllEnabled && items.length > 0 && (
        <button
          type="button"
          onClick={playAll}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-primary-500 bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-medium hover:bg-primary-100 dark:hover:bg-primary-900/50 transition-colors"
        >
          <Volume2 className="w-5 h-5" />
          Play All
        </button>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 gap-6">
        {items.map((card: VocabCardItem, i) => (
          <VocabCard key={card.id ?? i} card={card} />
        ))}
      </div>
    </div>
  );
};

function VocabCard({ card }: { card: VocabCardItem }) {
  const label = card.text ?? card.word ?? '—';
  const tts = (card.tts ?? card.text ?? card.word)?.trim();
  const imageUrl =
    typeof card.image === 'string'
      ? card.image
      : (card.image as { url?: string } | null)?.url ?? null;
  const play = useCallback(() => {
    if (tts) speakTamil(tts);
  }, [tts]);

  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-gray-800 shadow-sm overflow-hidden hover:shadow-md transition-shadow flex flex-col">
      <div className="aspect-[4/3] bg-slate-100 dark:bg-slate-700/50">
        <ImageWithFallback
          src={imageUrl}
          alt={label}
          className="h-full w-full object-cover"
        />
      </div>
      <div className="p-4 flex items-center justify-between gap-2">
        <span className="text-xl font-semibold text-gray-900 dark:text-white flex-1 truncate">
          {label}
        </span>
        <button
          type="button"
          onClick={play}
          aria-label={`Play: ${label}`}
          className="p-2 rounded-full bg-primary-100 dark:bg-primary-900/50 text-primary-600 dark:text-primary-400 hover:bg-primary-200 dark:hover:bg-primary-800/50 transition-colors shrink-0"
        >
          <Volume2 className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}
